import { Button, Card, CardProps, theme } from 'antd';
import { FC, ReactNode, createContext, memo, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { DrawerIconRight } from '@/common/components';
import { useCurrentRouteType, useGetRouteProps } from '@/common/hooks';
import { AppRouterTypes } from '@/common/interfaces';

import './CommonDrawer.scss';

interface Props {
  children: ReactNode;
}

type ContextType = {
  title: CardProps['title'];
  extra: CardProps['extra'];
  setTitle: (node: CardProps['title']) => void;
  setExtra: (node: CardProps['extra']) => void;
}

export const CommonDrawerContext = createContext<ContextType>({
  title: null as CardProps['title'],
  extra: null as CardProps['extra'],
  setTitle: () => {},
  setExtra: () => {}
});

export const CommonLayoutDrawer: FC<Props> = memo(({ children }) => {
  const { token } = theme.useToken();
  const location = useLocation();
  const commonDrawerWidthProp = useGetRouteProps<'commonDrawerWidth'>();
  const { routeType } = useCurrentRouteType();

  const [isCommonDrawerOpen, setIsCommonDrawerOpen] = useState(false);

  const [drawerTitle, setDrawerTitle] = useState<CardProps['title']>(null);
  const [drawerExtra, setDrawerExtra] = useState<CardProps['extra']>(null);

  const commonDrawerWidth = commonDrawerWidthProp?.commonDrawerWidth as number | string ?? 420;

  useEffect(() => {
    if (routeType === AppRouterTypes.Drawer) {
      // setTimeout(() => setIsCommonDrawerOpen(true), 350);
      setIsCommonDrawerOpen(true);
    } else {
      setIsCommonDrawerOpen(false);
    }
  }, [routeType, location.pathname]);

  const handleToggleCommonDrawer = (): void => {
    setIsCommonDrawerOpen((prev) => !prev);
  };

  const handleSetTitle = useCallback((node: ReactNode): void => {
    setDrawerTitle(node);
  }, []);

  const handleSetExtra = useCallback((node: ReactNode): void => {
    setDrawerExtra(node);
  }, []);

  return (
    <CommonDrawerContext.Provider
      value={{
        title: drawerTitle,
        extra: drawerTitle,
        setExtra: handleSetExtra,
        setTitle: handleSetTitle
      }}
    >
      <Button
        className="common-drawer__toggle-button"
        type="text"
        onClick={ handleToggleCommonDrawer }
        icon={ <DrawerIconRight/> }
      />

      <aside
        className="common-drawer__wrapper"
        style={{ height: '100%', width: isCommonDrawerOpen ? commonDrawerWidth : 0, }}
      >
        <div
          className="common-drawer"
          style={{
            width: commonDrawerWidth,
            borderLeft: `1px solid ${ token.colorBorder }`
          }}
        >
          <Card
            title={ drawerTitle || <>&nbsp;</> }
            variant='borderless'
            extra={ drawerExtra }
            styles={{ header: { borderBottom: `1px solid ${ token.colorBorder }` } }}
            rootClassName="common-drawer__container"
          >
            <div className="common-drawer__content">{ children }</div>
          </Card>
        </div>
      </aside>
    </CommonDrawerContext.Provider>
  );
});
